/**
 * Avatar — round icon-based avatar for chat bubbles (issue #351).
 *
 * Renders a lucide ``User`` (or ``Bot`` for the agent kind) inside a
 * coloured circle. The background hue is picked deterministically
 * from a stable hash of ``name`` so the same person always renders
 * with the same colour across sessions.
 */
import React from 'react';
import { Bot, User } from 'lucide-react';

export interface AvatarProps {
  /** Display name used to drive deterministic colour. */
  name: string;
  /** Diameter in px (default 32). */
  size?: number;
  /** Renders a bot icon instead of a user icon — for the agent kind. */
  bot?: boolean;
}

const PALETTE: ReadonlyArray<string> = [
  '#f59e0b', // amber
  '#10b981', // emerald
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#ef4444', // red
  '#14b8a6', // teal
  '#6366f1', // indigo
];

function hashString(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i += 1) {
    h = (h * 31 + input.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export const Avatar: React.FC<AvatarProps> = ({ name, size = 32, bot = false }) => {
  const bg = bot ? '#2563eb' : PALETTE[hashString(name) % PALETTE.length];
  const IconComp = bot ? Bot : User;
  return (
    <span
      className="chat-avatar"
      style={{
        width: size,
        height: size,
        background: bg,
      }}
      aria-hidden
    >
      <IconComp size={Math.round(size * 0.6)} color="#ffffff" strokeWidth={2} />
    </span>
  );
};
